import { jsxs, jsx } from "react/jsx-runtime";
import dedent from "dedent";
import { forwardRef } from "react";
import { useBackgroundLightness } from "../Box/BackgroundContext.mjs";
import { Box } from "../Box/Box.mjs";
import { Text } from "../Text/Text.mjs";
import { FieldOverlay } from "../private/FieldOverlay/FieldOverlay.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { root, bleedToCapHeight, realField, realFieldPosition, fieldSize, slideContainer, slideContainerSize, slideTrack, slideTrackMask, slideTrackBgLightMode, slideTrackBgDarkMode, slideTrackSelected, slider, hideBorderOnDarkBackgroundInLightMode, sliderThumbOutlineFix, focusOverlay, hoverOverlay } from "./Toggle.css.mjs";
import { virtualTouchable } from "../private/touchable/virtualTouchable.css.mjs";
const handleChange = (onChange) => (event) => {
  if (typeof onChange === "function") {
    onChange(event.target.checked);
  }
};
const Toggle = forwardRef(
  ({
    id,
    on,
    onChange,
    label,
    align = "left",
    togglePosition,
    size = "standard",
    bleedY: _bleedY,
    data,
    ...restProps
  }, forwardedRef) => {
    const lightness = useBackgroundLightness();
    if (process.env.NODE_ENV !== "production") {
      if (typeof _bleedY !== "undefined") {
        console.warn(
          dedent`
            ${_bleedY ? `The "bleedY" prop has been deprecated and will be removed in a future version. "bleedY" is enabled by default, so this prop can safely be removed with no layout impact.` : `The "bleedY" prop has been deprecated and will be removed in a future version. "bleedY" is now enabled by default. Please remove the "bleedY" prop and optimize your layout.`}`
        );
      }
    }
    const bleedY = _bleedY ?? true;
    const defaultTogglePosition = align === "left" ? "leading" : "trailing";
    const appliedTogglePosition = togglePosition || defaultTogglePosition;
    const alignToEnd = align === "left" && appliedTogglePosition === "trailing" || align !== "left" && appliedTogglePosition === "leading";
    return /* @__PURE__ */ jsxs(
      Box,
      {
        position: "relative",
        zIndex: 0,
        display: "flex",
        flexDirection: appliedTogglePosition === "trailing" ? "rowReverse" : "row",
        justifyContent: alignToEnd ? "flexEnd" : void 0,
        className: root,
        textAlign: "left",
        ...buildDataAttributes({ data, validateRestProps: restProps }),
        children: [
          /* @__PURE__ */ jsxs(
            Box,
            {
              position: "relative",
              className: bleedY && bleedToCapHeight[size],
              display: bleedY ? "flex" : void 0,
              alignItems: bleedY ? "center" : void 0,
              children: [
                /* @__PURE__ */ jsx(
                  Box,
                  {
                    component: "input",
                    type: "checkbox",
                    id,
                    checked: on,
                    onChange: handleChange(onChange),
                    position: "absolute",
                    zIndex: 1,
                    cursor: "pointer",
                    opacity: 0,
                    className: [
                      realField,
                      !bleedY && realFieldPosition[size],
                      fieldSize[size]
                    ],
                    ref: forwardedRef
                  }
                ),
                /* @__PURE__ */ jsxs(
                  Box,
                  {
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    className: [
                      slideContainer,
                      slideContainerSize[size],
                      fieldSize[size]
                    ],
                    children: [
                      /* @__PURE__ */ jsx(
                        Box,
                        {
                          position: "absolute",
                          width: "full",
                          overflow: "hidden",
                          borderRadius: "full",
                          className: [
                            slideTrack[size],
                            slideTrackMask,
                            slideTrackBgLightMode[lightness.lightMode],
                            slideTrackBgDarkMode[lightness.darkMode]
                          ],
                          children: /* @__PURE__ */ jsx(
                            Box,
                            {
                              position: "absolute",
                              width: "full",
                              height: "full",
                              background: "formAccent",
                              transition: "fast",
                              className: slideTrackSelected
                            }
                          )
                        }
                      ),
                      /* @__PURE__ */ jsxs(
                        Box,
                        {
                          position: "absolute",
                          background: "surface",
                          transition: "fast",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "full",
                          className: slider[size],
                          children: [
                            /* @__PURE__ */ jsx(
                              FieldOverlay,
                              {
                                variant: on ? "formAccent" : "default",
                                borderRadius: "full",
                                visible: true,
                                className: {
                                  [sliderThumbOutlineFix]: true,
                                  [hideBorderOnDarkBackgroundInLightMode]: lightness.lightMode === "dark"
                                }
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              FieldOverlay,
                              {
                                variant: "focus",
                                borderRadius: "full",
                                className: focusOverlay
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              FieldOverlay,
                              {
                                variant: "formAccent",
                                borderRadius: "full",
                                className: !on ? hoverOverlay : void 0
                              }
                            )
                          ]
                        }
                      )
                    ]
                  }
                )
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            Box,
            {
              component: "label",
              htmlFor: id,
              paddingLeft: appliedTogglePosition === "trailing" ? void 0 : "xsmall",
              paddingRight: appliedTogglePosition === "leading" ? void 0 : "xsmall",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flexGrow: align === "justify" ? 1 : void 0,
              userSelect: "none",
              cursor: "pointer",
              className: virtualTouchable,
              children: /* @__PURE__ */ jsx(
                Text,
                {
                  baseline: bleedY,
                  weight: on ? "strong" : void 0,
                  size,
                  align: align === "justify" && appliedTogglePosition === "leading" ? "right" : void 0,
                  children: label
                }
              )
            }
          )
        ]
      }
    );
  }
);
export {
  Toggle
};
